import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase/client";
import { AgendaEvent } from "@/types";

/**
 * Reveal Hint type from database
 */
export interface RevealHint {
  id: string;
  event_id: string;
  hint_number: number;
  hint_text: string;
  hint_image_url?: string | null;
  release_at: string;
  is_released: boolean;
  released_at?: string | null;
  lottie_animation?: string | null;
  created_at: string;
}

/**
 * Reveal Challenge type from database
 */
export interface RevealChallenge {
  id: string;
  event_id: string;
  title: string;
  description?: string | null;
  challenge_type: "quiz" | "photo" | "location" | "task" | "puzzle";
  required_value?: number | null;
  current_value: number;
  is_completed: boolean;
  completed_at?: string | null;
  completed_by_team_id?: string | null;
  deadline?: string | null;
  reward_text?: string | null;
  lottie_animation?: string | null;
  order_index: number;
  created_by?: string | null;
  created_at: string;
}

/**
 * Challenge Submission type
 */
export interface ChallengeSubmission {
  id: string;
  challenge_id: string;
  team_id: string;
  submitted_by: string;
  submission_type: string;
  submission_value?: string | null;
  submission_file_url?: string | null;
  is_approved?: boolean | null;
  approved_by?: string | null;
  approved_at?: string | null;
  points_awarded?: number | null;
  created_at: string;
}

interface UseRevealOptions {
  eventId: string;
  enabled?: boolean;
}

interface UseRevealReturn {
  hints: RevealHint[];
  challenges: RevealChallenge[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  // Hint actions
  releaseHint: (hintId: string) => Promise<boolean>;
  // Challenge actions
  submitChallenge: (challengeId: string, submission: Partial<ChallengeSubmission>) => Promise<boolean>;
  completeChallenge: (challengeId: string, teamId: string) => Promise<boolean>;
  // Admin actions
  addHint: (hint: Omit<RevealHint, "id" | "created_at" | "is_released" | "released_at">) => Promise<boolean>;
  addChallenge: (challenge: Omit<RevealChallenge, "id" | "created_at" | "is_completed" | "completed_at" | "completed_by_team_id" | "current_value">) => Promise<boolean>;
  deleteHint: (hintId: string) => Promise<boolean>;
  deleteChallenge: (challengeId: string) => Promise<boolean>;
  // Reveal control
  triggerReveal: (eventId: string) => Promise<boolean>;
}

/**
 * Hook for managing event reveals, hints, and challenges
 */
export const useReveal = ({ eventId, enabled = true }: UseRevealOptions): UseRevealReturn => {
  const [hints, setHints] = useState<RevealHint[]>([]);
  const [challenges, setChallenges] = useState<RevealChallenge[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch hints and challenges for the event
   */
  const fetchData = useCallback(async () => {
    if (!eventId || !enabled) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Fetch hints
      const { data: hintsData, error: hintsError } = await supabase
        .from("reveal_hints")
        .select("*")
        .eq("event_id", eventId)
        .order("hint_number", { ascending: true });

      if (hintsError) throw hintsError;

      // Fetch challenges
      const { data: challengesData, error: challengesError } = await supabase
        .from("reveal_challenges")
        .select("*")
        .eq("event_id", eventId)
        .order("order_index", { ascending: true });

      if (challengesError) throw challengesError;

      setHints(hintsData || []);
      setChallenges(challengesData || []);
    } catch (err: any) {
      console.error("Error fetching reveal data:", err);
      setError(err.message || "Could not load reveal data");
    } finally {
      setIsLoading(false);
    }
  }, [eventId, enabled]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /**
   * Release a hint (make it visible)
   */
  const releaseHint = useCallback(async (hintId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from("reveal_hints")
        .update({
          is_released: true,
          released_at: new Date().toISOString(),
        })
        .eq("id", hintId);

      if (error) throw error;

      // Update local state
      setHints((prev) =>
        prev.map((hint) =>
          hint.id === hintId
            ? { ...hint, is_released: true, released_at: new Date().toISOString() }
            : hint
        )
      );

      return true;
    } catch (err: any) {
      console.error("Error releasing hint:", err);
      return false;
    }
  }, []);

  /**
   * Submit a challenge answer/proof
   */
  const submitChallenge = useCallback(
    async (challengeId: string, submission: Partial<ChallengeSubmission>): Promise<boolean> => {
      try {
        const { error } = await supabase.from("challenge_submissions").insert({
          challenge_id: challengeId,
          ...submission,
        });

        if (error) throw error;
        return true;
      } catch (err: any) {
        console.error("Error submitting challenge:", err);
        return false;
      }
    },
    []
  );

  /**
   * Mark a challenge as completed (admin action)
   */
  const completeChallenge = useCallback(
    async (challengeId: string, teamId: string): Promise<boolean> => {
      try {
        const { error } = await supabase
          .from("reveal_challenges")
          .update({
            is_completed: true,
            completed_at: new Date().toISOString(),
            completed_by_team_id: teamId,
          })
          .eq("id", challengeId);

        if (error) throw error;

        // Update local state
        setChallenges((prev) =>
          prev.map((challenge) =>
            challenge.id === challengeId
              ? {
                  ...challenge,
                  is_completed: true,
                  completed_at: new Date().toISOString(),
                  completed_by_team_id: teamId,
                }
              : challenge
          )
        );

        return true;
      } catch (err: any) {
        console.error("Error completing challenge:", err);
        return false;
      }
    },
    []
  );

  /**
   * Add a new hint (admin action)
   */
  const addHint = useCallback(
    async (
      hint: Omit<RevealHint, "id" | "created_at" | "is_released" | "released_at">
    ): Promise<boolean> => {
      try {
        const { data, error } = await supabase
          .from("reveal_hints")
          .insert({
            ...hint,
            is_released: false,
          })
          .select()
          .single();

        if (error) throw error;

        setHints((prev) => [...prev, data].sort((a, b) => a.hint_number - b.hint_number));
        return true;
      } catch (err: any) {
        console.error("Error adding hint:", err);
        return false;
      }
    },
    []
  );

  /**
   * Add a new challenge (admin action)
   */
  const addChallenge = useCallback(
    async (
      challenge: Omit<
        RevealChallenge,
        "id" | "created_at" | "is_completed" | "completed_at" | "completed_by_team_id" | "current_value"
      >
    ): Promise<boolean> => {
      try {
        const { data, error } = await supabase
          .from("reveal_challenges")
          .insert({
            ...challenge,
            is_completed: false,
            current_value: 0,
          })
          .select()
          .single();

        if (error) throw error;

        setChallenges((prev) => [...prev, data].sort((a, b) => a.order_index - b.order_index));
        return true;
      } catch (err: any) {
        console.error("Error adding challenge:", err);
        return false;
      }
    },
    []
  );

  /**
   * Delete a hint (admin action)
   */
  const deleteHint = useCallback(async (hintId: string): Promise<boolean> => {
    try {
      const { error } = await supabase.from("reveal_hints").delete().eq("id", hintId);

      if (error) throw error;

      setHints((prev) => prev.filter((hint) => hint.id !== hintId));
      return true;
    } catch (err: any) {
      console.error("Error deleting hint:", err);
      return false;
    }
  }, []);

  /**
   * Delete a challenge (admin action)
   */
  const deleteChallenge = useCallback(async (challengeId: string): Promise<boolean> => {
    try {
      const { error } = await supabase.from("reveal_challenges").delete().eq("id", challengeId);

      if (error) throw error;

      setChallenges((prev) => prev.filter((c) => c.id !== challengeId));
      return true;
    } catch (err: any) {
      console.error("Error deleting challenge:", err);
      return false;
    }
  }, []);

  /**
   * Trigger the reveal (reveal the event/location)
   */
  const triggerReveal = useCallback(async (eventIdToReveal: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from("agenda_events")
        .update({
          is_revealed: true,
          updated_at: new Date().toISOString(),
        })
        .eq("id", eventIdToReveal);

      if (error) throw error;
      return true;
    } catch (err: any) {
      console.error("Error triggering reveal:", err);
      return false;
    }
  }, []);

  return {
    hints,
    challenges,
    isLoading,
    error,
    refetch: fetchData,
    releaseHint,
    submitChallenge,
    completeChallenge,
    addHint,
    addChallenge,
    deleteHint,
    deleteChallenge,
    triggerReveal,
  };
};

export default useReveal;

